import {
  TagReadable,
  tagsCreateMutation,
  tagsDestroyMutation,
  tagsListOptions,
  tagsListQueryKey,
  tagsPartialUpdateMutation,
  tagsRetrieveOptions
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  FormModal,
  FormNomenclaturesTags
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryUpdate,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Tag } from 'antd'
import { Add, Edit, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'tags'

export const NomenclaturesTags = () => {
  const { t } = useLocales()
  const { params } = useQueryUpdate()
  const sorter = useTableSorter()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()
  const { resetPagination } = useResetPagination()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: tags, isLoading } = useQuery(
    tagsListOptions({ query: { ...defaultFilters, ...params } })
  )

  const deleteTagsMutation = useMutation({
    ...tagsDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([tagsListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItem) {
      deleteTagsMutation.mutate({ path: { id: deleteItem } })
    }
  }

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
    setDeleteItem(undefined)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        icon: <Edit size={20} />,
        key: 'edit',
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        icon: <Trash size={20} />,
        danger: true,
        key: 'delete',
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<TagReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.color'),
        key: 'color',
        sorter: true,
        render: ({ color }) => (
          <Tag key={color} color={color}>
            {color}
          </Tag>
        )
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TableContainer
        data={tags}
        rightChildren={
          <Button
            type="primary"
            icon={<Add size={20} />}
            onClick={() => setIsCreate(true)}
          >
            {t('nomenclature.add_tag')}
          </Button>
        }
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCloseModal}
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={tagsRetrieveOptions}
        onCreate={tagsCreateMutation}
        onUpdate={tagsPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclaturesTags />
      </FormModal>
    </>
  )
}
